import { Chip, IconButton, Paper, TextField, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { getTokens, brandOn } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        root: {
            width: "100%",
            padding: 0,
            border: 0,
            borderRadius: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
        },
        tagButton: {
            width: 32,
            height: 32,
            padding: 0,
            borderRadius: t.radius.sm,
            color: t.textMuted,
            border: `1px solid ${t.line}`,
            background: t.paper,
            "&:hover": {
                color: t.accentHover,
                borderColor: t.accentTint.strong,
                background: t.accentTint.soft,
            },
            "& .MuiSvgIcon-root": { fontSize: 17 },
        },
        autocomplete: {
            width: "100%",
            "& .MuiOutlinedInput-root": {
                minHeight: 32,
                padding: "2px 34px 2px 6px !important",
                borderRadius: t.radius.sm,
                backgroundColor: t.paper,
                fontSize: 11.5,
            },
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: t.line,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: t.line,
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${t.accent} !important`,
            },
            "& .MuiAutocomplete-input": {
                minWidth: "54px !important",
                padding: "3px !important",
            },
            "& .MuiAutocomplete-endAdornment": { top: "calc(50% - 14px)" },
            "& .MuiChip-root": { maxWidth: 104 },
        },
    };
});

export function TagsContainer({ contact }) {

    const classes = useStyles();

    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const [editing, setEditing] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            loadTags().then(() => {
                if (Array.isArray(contact.tags)) {
                    setSelecteds(contact.tags);
                } else {
                    setSelecteds([]);
                }
            });
        }
    }, [contact]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`, 
            {params: { kanban: 0}
        });
            setTags(data);
        } catch (err) {
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (item.length < 3) {
                        toastError("Tag muito curta!");
                        return;
                    }
                    if (isString(item)) {
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        setSelecteds(optionsChanged);
        await syncTags({ contactId: contact.id, tags: optionsChanged });
    }

    function getRandomHexColor() {
        // Gerar valores aleatórios para os componentes de cor
        const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255
      
        // Converter os componentes de cor em uma cor hexadecimal
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
      
        return hexColor;
    }

    return (
        <Paper className={classes.root}>
            {!editing && selecteds.length === 0 ? (
                <Tooltip title="Adicionar tag">
                    <IconButton
                        className={classes.tagButton}
                        size="small"
                        aria-label="Adicionar tag"
                        onClick={() => setEditing(true)}
                    >
                        <LocalOfferOutlinedIcon />
                    </IconButton>
                </Tooltip>
            ) : (
              <Autocomplete
                className={classes.autocomplete}
                multiple
                size="small"
                options={tags}
                value={selecteds}
                freeSolo
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => isString(option) ? option : option?.name || ""}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            style={{
                                // cor do cadastro da tag; texto escuro ou branco conforme o contraste
                                ...(option.color
                                    ? { backgroundColor: option.color, color: brandOn(option.color) }
                                    : {}),
                                marginRight: 3,
                                height: 22,
                                fontWeight: 600,
                                fontSize: 11,
                                whiteSpace: "nowrap"
                            }}
                            label={option.name}
                            {...getTagProps({ index })}
                            size="small"
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        autoFocus={editing}
                        variant="outlined"
                        placeholder="Adicionar tag"
                        onKeyDown={(event) => {
                            if (event.key === "Escape" && selecteds.length === 0) {
                                setEditing(false);
                            }
                        }}
                    />
                )}
                PaperComponent={({ children }) => (
                    <Paper
                        style={{ width: "min(400px, calc(100vw - 32px))", marginLeft: 6 }}
                    >
                        {children}
                    </Paper>
                )}
              />
            )}
        </Paper>
    )
}
